import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, map, mergeMap } from 'rxjs';
import { JsonConvert } from 'json2typescript';

import { HttpUtilsService } from '../../commons/http-uilis.service';
import { Paging } from '../../../model/paging/paging';
import { environment } from '../../../environment/environment';
import { keycloakLogout } from '../../../auth/keycloak.config';
import { Amplifier, AmplifierData, AmplifierDataArray } from '../../../model/amplifier/amplifier';

@Injectable({
  providedIn: 'root'
})
export class AmplifierService {
  private _url: string = `${environment.baseAPIURL}/settings`;

  constructor(private _http: HttpClient, private _httpUtils: HttpUtilsService) {}

  GETPaginatedAmplifiers(paging: Paging): Observable<AmplifierDataArray> | any {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap((httpHeaders) => {
          return this._http.get(`${this._url}/amplifiers${this.buildQueryString(paging)}`, { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              const jsonConvert: JsonConvert = new JsonConvert();
              const response: AmplifierDataArray = jsonConvert.deserializeObject(res, AmplifierDataArray);
              if (response.success) {
                return response;
              } else {
                this._httpUtils.handleError(response);
                return;
              }
            }, catchError((err) => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError((err) => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  GETAmplifierById(amplifierId: number): Observable<AmplifierData> | any {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap((httpHeaders) => {
          return this._http.get(`${this._url}/amplifiers/${amplifierId}`, { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              const jsonConvert: JsonConvert = new JsonConvert();
              const response: AmplifierData = jsonConvert.deserializeObject(res, AmplifierData);
              if (response.success) {
                return response;
              } else {
                this._httpUtils.handleError(response);
                return;
              }
            }, catchError((err) => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError((err) => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  POSTAmplifier(body: Amplifier) {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap((httpHeaders) => {
          return this._http.post(`${this._url}/amplifiers`, this.toCreatePayload(body), { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              return res;
            }, catchError((err) => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError((err) => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  PUTAmplifier(amplifierId: number, body: Amplifier) {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap((httpHeaders) => {
          return this._http.put(`${this._url}/amplifiers/${amplifierId}`, this.toUpdatePayload(body), { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              return res;
            }, catchError((err) => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError((err) => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  DELETEAmplifier(amplifierId: number) {
    try {
      return this._httpUtils.getHTTPHeadersAsync().pipe(
        mergeMap((httpHeaders) => {
          return this._http.delete(`${this._url}/amplifiers/${amplifierId}`, { headers: httpHeaders, withCredentials: true }).pipe(
            map((res: any) => {
              return res;
            }, catchError((err) => {
              if (err.status === 401) {
                keycloakLogout({ redirectUri: window.location.origin });
              }
              throw err;
            }))
          );
        }),
        catchError((err) => {
          throw err;
        })
      );
    } catch (error) {
      throw error;
    }
  }

  private buildQueryString(paging: Paging): string {
    const params = new URLSearchParams({
      offset: String((paging.pageIndex - 1) * paging.pageSize),
      limit: String(paging.pageSize)
    });
    const sort = paging.sort.find((item) => item.value !== null);
    if (sort?.key) {
      params.set('sortBy', sort.key);
    }
    if (sort?.value === 'ascend') {
      params.set('sortOrder', 'asc');
    } else if (sort?.value === 'descend') {
      params.set('sortOrder', 'desc');
    }
    return `?${params.toString()}`;
  }

  private toCreatePayload(body: Amplifier) {
    return {
      name: body.name,
      gain: {
        freq_24: body.gain?.freq_24 ?? 0,
        freq_5: body.gain?.freq_5 ?? 0,
        freq_6: body.gain?.freq_6 ?? 0
      },
      note: body.note || null,
      components: (body.components ?? []).map((component) => ({
        code: component.code,
        value: component.value
      }))
    };
  }

  private toUpdatePayload(body: Amplifier) {
    return {
      gain: {
        freq_24: body.gain?.freq_24 ?? 0,
        freq_5: body.gain?.freq_5 ?? 0,
        freq_6: body.gain?.freq_6 ?? 0
      },
      note: body.note || null,
      components: (body.components ?? []).map((component) => ({
        code: component.code,
        value: component.value
      }))
    };
  }
}
